import { Box, FormControl, Input, Typography } from "@mui/joy";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import moment from "moment";
import React from "react";
import { useNavigate } from "react-router-dom";
import PortalTableLayout from "../components/PortalTableLayout";
import { isAdminRole, useRole } from "../Contexts/RoleContext";
import { Util } from "../Helpers/Util";

interface ServiceReportRow {
	service_reports_id: string;
	report_date: string;
	entered_by: string;
	device_saved_at: string;
	server_received_at: string;
	building_count: number;
}

const columns = ["Date", "Entered By", "Saved on Phone", "Received", "Buildings"];

export default function ServiceReports() {
	const { effectiveRole } = useRole();
	const navigate = useNavigate();
	const [rows, setRows] = React.useState<ServiceReportRow[]>([]);
	const [search, setSearch] = React.useState("");
	const [loading, setLoading] = React.useState(true);
	const [error, setError] = React.useState("");
	const [orderBy, setOrderBy] = React.useState(0);
	const [desc, setDesc] = React.useState(true);
	const [page, setPage] = React.useState(0);
	const [pageSize, setPageSize] = React.useState(25);
	const staff = isAdminRole(effectiveRole);

	React.useEffect(() => {
		if (!staff) return;
		const handle = window.setTimeout(() => {
			setLoading(true);
			fetch("ServiceReport/GetServiceReports", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ search }),
			})
				.then((response) => response.json())
				.then((body) => {
					if (body && body.error) {
						setError(body.error);
						setRows([]);
					} else {
						setError("");
						setRows(body ?? []);
					}
					setLoading(false);
				})
				.catch(() => {
					setError("Could not load service reports.");
					setLoading(false);
				});
		}, 300);
		return () => window.clearTimeout(handle);
	}, [search, staff]);

	const sorted = [...rows].sort((a, b) => {
		const dir = desc ? -1 : 1;
		switch (columns[orderBy]) {
			case "Entered By":
				return dir * (a.entered_by ?? "").localeCompare(b.entered_by ?? "");
			case "Saved on Phone":
				return dir * (moment(a.device_saved_at).valueOf() - moment(b.device_saved_at).valueOf());
			case "Received":
				return dir * (moment(a.server_received_at).valueOf() - moment(b.server_received_at).valueOf());
			case "Buildings":
				return dir * ((a.building_count ?? 0) - (b.building_count ?? 0));
			default:
				return dir * (moment(a.report_date).valueOf() - moment(b.report_date).valueOf());
		}
	});
	const pageRows = sorted.slice(page * pageSize, (page + 1) * pageSize);

	if (!staff) return <Typography>Service reports are not available for this login.</Typography>;

	return (
		<PortalTableLayout
			fillHeight
			toolbar={
				<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1.5, flexWrap: "wrap", backgroundColor: "background.surface", border: "1px solid", borderColor: "divider", p: 1.5 }}>
					<Typography level="title-lg" sx={{ m: 0 }}>
						Service Reports
					</Typography>
					<FormControl>
						<Input
							placeholder="Search"
							value={search}
							onChange={(event) => {
								setSearch(event.target.value);
								setPage(0);
							}}
						/>
					</FormControl>
				</Box>
			}
			pagination={{
				count: sorted.length,
				page,
				rowsPerPage: pageSize,
				onPageChange: (_event, next) => setPage(next),
				onRowsPerPageChange: (event) => {
					setPageSize(parseInt(event.target.value, 10));
					setPage(0);
				},
			}}>
			<Table stickyHeader size="small">
				<TableHead>
					<TableRow>
						{columns.map((column, index) => (
							<TableCell key={column}>
								<TableSortLabel
									active={orderBy === index}
									direction={orderBy === index && desc ? "desc" : "asc"}
									onClick={() => {
										setDesc(orderBy === index ? !desc : true);
										setOrderBy(index);
									}}>
									{column}
								</TableSortLabel>
							</TableCell>
						))}
					</TableRow>
				</TableHead>
				<TableBody>
					{loading && (
						<TableRow>
							<TableCell colSpan={columns.length} align="center">
								<CircularProgress size={28} />
							</TableCell>
						</TableRow>
					)}
					{!loading && error && (
						<TableRow>
							<TableCell colSpan={columns.length}>{error}</TableCell>
						</TableRow>
					)}
					{!loading && !error && pageRows.length === 0 && (
						<TableRow>
							<TableCell colSpan={columns.length}>No service reports.</TableCell>
						</TableRow>
					)}
					{!loading &&
						!error &&
						pageRows.map((row) => (
							<TableRow key={row.service_reports_id} hover sx={{ cursor: "pointer" }} onClick={() => navigate("/servicereports/" + row.service_reports_id)}>
								<TableCell>{Util.Formatter(row.report_date, "D")}</TableCell>
								<TableCell>{row.entered_by}</TableCell>
								<TableCell>{Util.Formatter(row.device_saved_at, "DT")}</TableCell>
								<TableCell>{Util.Formatter(row.server_received_at, "DT")}</TableCell>
								<TableCell>{row.building_count}</TableCell>
							</TableRow>
						))}
				</TableBody>
			</Table>
		</PortalTableLayout>
	);
}
